import React, { useMemo } from "react";
import { memo } from "react";
import classNames from "classnames";

// components
import { format, isEmpty, useEffectOnce } from "@swiggy-private/core-ui";
import { default as Plus } from "@swiggy-private/dls-web-icons/dist/web/generic/plus/12";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// constants
import { CART_ITEMS_WIDGET } from "@cart-ui/constants/TextDict";
import { ACCESSIBILITY_TEXTS } from "@cart-ui/constants";

//styles
import styles from "./styles/CartAddItems.scss";
import { ContentButtonStyled, IconStyled, TextWrapper } from "./CartAddItemsV2.style";

// helpers
import { replaceAmountToAddAndSavings } from "@cart-ui/helpers/CouponModal";

// types
import { CART_ADD_ITEMS_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { CartAddItemsProps } from "./CartAddItems";

function CartAddItemsV2(props: CartAddItemsProps): React.ReactElement {
    const { couponSavingsInfo, onClick, onImpressionEvent, autoAddPlan, text } = props;

    const isPlainAddMoreText = useMemo(
        (): boolean => isEmpty(couponSavingsInfo) || isEmpty(couponSavingsInfo.amount_to_add),
        [couponSavingsInfo],
    );

    const savingsClassName = classNames(styles.cartV2PriceBreathe, styles.cartV2PriceBreatheDelayed);
    const amountToAddClassName = classNames(styles.cartV2PriceBreathe, styles.cartV2PriceBreatheAbsolute);

    const addText = useMemo(() => {
        if (isPlainAddMoreText) {
            return text ?? CART_ITEMS_WIDGET.ADD_MORE_ITEMS;
        }

        return replaceAmountToAddAndSavings(
            couponSavingsInfo?.comms || "",
            `<span class="${styles.cartV2AmountToAddContainer}">
                <span class="${styles.cartV2AmountToAddInvisible}">${couponSavingsInfo?.amount_to_add}</span>
                <span class="${amountToAddClassName}">${couponSavingsInfo?.amount_to_add}</span>
            </span>`,
            `<span class="${savingsClassName}">${couponSavingsInfo?.savings}</span>`,
        );
    }, [
        couponSavingsInfo?.amount_to_add,
        couponSavingsInfo?.savings,
        isPlainAddMoreText,
        savingsClassName,
        couponSavingsInfo?.comms,
        amountToAddClassName,
        text,
    ]);

    const accessbilityText = useMemo((): string => {
        if (isPlainAddMoreText) {
            return ACCESSIBILITY_TEXTS.ADD_MORE_ITEMS;
        }

        return format(
            ACCESSIBILITY_TEXTS.ADD_MORE_ITEMS_WORTH,
            couponSavingsInfo?.amount_to_add || "",
            couponSavingsInfo?.savings || "",
        );
    }, [couponSavingsInfo?.amount_to_add, couponSavingsInfo?.savings, isPlainAddMoreText]);

    useEffectOnce(() => {
        if (!isPlainAddMoreText) {
            onImpressionEvent(false);
        }
    });

    return (
        <ContentButtonStyled
            isAutoAddXp={!isEmpty(autoAddPlan)}
            onClick={onClick}
            data-testid={CART_ADD_ITEMS_TEST_IDS.BUTTON}
            aria-label={accessbilityText}
        >
            <IconStyled as="span">
                <GenericIcon Icon={Plus} strokeColor="text_Med_Emphasis" fillColor="text_Med_Emphasis" />
            </IconStyled>
            <TextWrapper>
                <Text
                    className={classNames({
                        [styles.cartV2ContentText]: !isEmpty(couponSavingsInfo),
                    })}
                    color="text_Med_Emphasis"
                    variant="V2.body3.neutral.medium"
                    data-testid={CART_ADD_ITEMS_TEST_IDS.TEXT}
                    dangerouslySetInnerHTML={{ __html: addText }}
                />
            </TextWrapper>
        </ContentButtonStyled>
    );
}

export default memo(CartAddItemsV2);
